import type { CountryGroup, ExperimentBucket } from "./experiment";

/** Shared server/browser experiment configuration; keep this module import-light. */

/**
 * The pro-cms path the homepage document is read from today: `home-page` is
 * a Strapi 5 **single type**, so the bare route is the whole addressing
 * scheme — one document, no id.
 */
export const CONTROL_HOMEPAGE_CMS_PATH = "/api/home-page";

/**
 * Arm → pro-cms path for that arm's homepage document.
 *
 * A tier-1 homepage test (headline, sub-copy, CTA text, an image) is just a
 * second CMS document, which is why this is a path and not a code branch:
 * once an arm points somewhere else, marketing writes arm B themselves and
 * engineering is not involved in each test.
 *
 * TODO(product): supply the arm-B document path before running a homepage
 * test. It cannot be another `/api/home-page` — a single type holds exactly
 * one document — so arm B has to be an entry in a sibling collection type,
 * and the value here is that entry's full read path including its Strapi 5
 * documentId, e.g. `/api/home-page-variants/abc123`. Whatever collection is
 * chosen must answer the same shape `home-page` does (see
 * lib/api/home/adapter.ts), or the adapter drops the sections silently.
 */
export const HOMEPAGE_CMS_PATH_BY_BUCKET: Record<ExperimentBucket, string> = {
  a: CONTROL_HOMEPAGE_CMS_PATH,
  b: CONTROL_HOMEPAGE_CMS_PATH,
  c: CONTROL_HOMEPAGE_CMS_PATH,
  d: CONTROL_HOMEPAGE_CMS_PATH,
};

export function resolveHomepageCmsPath(
  bucket?: ExperimentBucket | null,
): string {
  return bucket
    ? HOMEPAGE_CMS_PATH_BY_BUCKET[bucket]
    : CONTROL_HOMEPAGE_CMS_PATH;
}

/**
 * True once an arm reads its homepage from a document that is not the live
 * one. This is what arms the SEO guard: an experiment document carries
 * whatever canonical / robots fields its author happened to fill in, and
 * those must never reach the page (see (home)/homepageSeo.ts).
 */
export function isHomepageExperimentDocument(
  bucket?: ExperimentBucket | null,
): boolean {
  return resolveHomepageCmsPath(bucket) !== CONTROL_HOMEPAGE_CMS_PATH;
}

/**
 * Arm → origin path a whole-different-homepage (tier 3) variant is served
 * from, for the case where an arm outgrows a CMS document and needs its own
 * route. The Worker keeps serving it under `/`, exactly as
 * `HOMEPAGE_PATHNAME` already does.
 *
 * Empty today, and most tests should never need an entry — tier 1 (a CMS
 * document) and tier 2 (a prop into V2HomeBody) both stay on `/`.
 *
 * Filling one in is what makes `next.config.ts` redirect the bare path, so
 * the variant can never become a second homepage that search engines find.
 * That guard is why the paths live in config rather than being implied by
 * the existence of a route file: a route added without an entry here ships
 * unprotected, and the reviewer has one list to check.
 */
export const HOMEPAGE_VARIANT_PATH_BY_BUCKET: Record<
  ExperimentBucket,
  string | null
> = {
  a: null,
  b: null,
  c: null,
  d: null,
};

/**
 * Every configured variant path, de-duplicated, in bucket order. Two arms
 * sharing one path is a legitimate split of a tier-3 test; emitting the same
 * redirect twice is not.
 */
export function dedupeVariantPaths(
  map: Record<ExperimentBucket, string | null>,
): string[] {
  return [
    ...new Set(Object.values(map).filter((path): path is string => !!path)),
  ];
}

/** The paths next.config.ts redirects to `/`. */
export function homepageVariantPaths(): string[] {
  return dedupeVariantPaths(HOMEPAGE_VARIANT_PATH_BY_BUCKET);
}

/**
 * The catalogue served when the edge sent no country group — local and
 * preview environments have no Worker in front of them, so this is what they
 * render. It is a shipped catalogue, not the legacy v11 one, which means a
 * group-less production request (Googlebot, a cache miss on the header) now
 * gets v25. v25/v26 are the guest-seat lineups; the country group picks
 * between them independently of the homepage experiment bucket.
 */
export const DEFAULT_PRICING_CATALOG_VERSION = "v25";

// Same country eligibility and flag names as pro-api; the catalogue is chosen
// by country group alone, so every bucket in an eligible group sees it.
export const PRICING_FLAGS = {
  bronze_row_pricing: {
    groups: ["bronze", "row"],
    version: "v25",
  },
  platinum_gold_silver_pricing: {
    groups: ["platinum", "gold", "silver"],
    version: "v26",
  },
} satisfies Record<string, { groups: CountryGroup[]; version: string }>;

export function resolvePricingCatalogVersion(
  group?: CountryGroup | null,
): string {
  if (!group) return DEFAULT_PRICING_CATALOG_VERSION;
  for (const flag of Object.values(PRICING_FLAGS)) {
    if ((flag.groups as readonly string[]).includes(group)) return flag.version;
  }
  return DEFAULT_PRICING_CATALOG_VERSION;
}
